import {useMemo} from "react";
import {useQueries, useQuery} from "@tanstack/react-query";
import {useShowError} from "@hooks/useShowError/useShowError";
import {getSyncPreviewVoyageTemplateSingleVoyage} from "@containers/singleVoyage/voyageTemplates/api/api";
import {
	QUERY_KEY_VOYAGE_TEMPLATE_OUTDATED_SINGLE_VOYAGES
} from "@containers/singleVoyage/voyageTemplates/queryHooks/constants/queryKey";
import {ISimpleSingleVoyage, VoyageStatusSingleVoyage} from "@containers/singleVoyage/types/voyage";

/** Statuses the backend syncs; a started or finished voyage never follows its template again. */
const SYNCABLE_STATUSES: VoyageStatusSingleVoyage[] = ['DRAFT', 'READY_FOR_MANUAL_RUN', 'PLANNED_FOR_AUTORUN']

const outdatedQuery = (accountId: string | number, templateId: number, onShowError: ReturnType<typeof useShowError>['onShowError']) => ({
	queryKey: [QUERY_KEY_VOYAGE_TEMPLATE_OUTDATED_SINGLE_VOYAGES, accountId?.toString(), templateId],
	queryFn: () => getSyncPreviewVoyageTemplateSingleVoyage({accountId, templateId}, onShowError),
	staleTime: 1000 * 60,
	refetchOnWindowFocus: false,
	enabled: !!accountId,
})

/**
 * Ids of the given voyages that differ from their template as it is now. Uses the sync preview,
 * one request per template on the page, so it agrees with what the sync window will show.
 */
export function useOutdatedVoyagesSingleVoyage({accountId, voyages}: {
	accountId: string | number
	voyages: Pick<ISimpleSingleVoyage, 'id' | 'voyageTemplateId' | 'status'>[]
}): Set<number> {

	const {onShowError} = useShowError()

	const templateIds = useMemo(() => {
		return Array.from(new Set((voyages ?? [])
			.filter((voyage) => voyage?.voyageTemplateId && SYNCABLE_STATUSES.includes(voyage.status))
			.map((voyage) => voyage.voyageTemplateId)))
	}, [voyages])

	const results = useQueries({
		queries: templateIds.map((templateId) => outdatedQuery(accountId, templateId, onShowError)),
	})

	const dataKey = results.map((result) => result.dataUpdatedAt).join(',')

	return useMemo(() => {
		return new Set(results.flatMap((result) => result.data ?? []).map((item) => item.voyageId))
	}, [dataKey])
}

/** How many planned voyages of one template differ from it now; skipped when it has none planned. */
export function useOutdatedCountOfTemplateSingleVoyage({accountId, templateId, plannedVoyageCount}: {
	accountId: string | number
	templateId: number
	plannedVoyageCount: number
}): number {

	const {onShowError} = useShowError()

	const {data} = useQuery({
		...outdatedQuery(accountId, templateId, onShowError),
		enabled: !!accountId && plannedVoyageCount > 0,
	})

	return plannedVoyageCount > 0 ? data?.length ?? 0 : 0
}
